import type {
  ErpAiKbCandidatesQuery,
  ErpAiKbReviewerCandidatesQuery,
  ErpAiKbReviewerMeQuery,
  ErpAiKbReviewersQuery,
  ErpAiKnowledgeDocumentQuery,
  ErpAiKnowledgeDocumentsQuery,
  ErpAiKnowledgeRevisionsQuery,
  ErpAiKnowledgeSearchQuery,
  ErpAiKnowledgeStatsQuery,
} from 'gqlhooks';
import { communicatorLink } from 'core/utils/communicatorDeepLink';
import { PATHS } from 'router/config';

/**
 * ТЗ 0104, §3.8 / §4 «База знаний» — чистая модель вкладки «Wiedza»:
 * категории, статусы и стратегии (строки сервера — graphene-енумов нет),
 * переходы статуса, параметры адреса, проверка файла до загрузки, оценка
 * токенов и коды отказов.
 *
 * Без React — тестируется напрямую. Импортируется прямым путём
 * (`core/hooks/AiKnowledge/aiKnowledgeModel`), не через барабан
 * `core/hooks`: там имена потоков столкнулись бы. Все имена — с префиксом
 * `aiKb` / `AI_KB_`.
 */

// ── Типы ответа ────────────────────────────────────────────────────────────

export type AiKbRow = ErpAiKnowledgeDocumentsQuery['aiKnowledgeDocuments'][number];
export type AiKbDocument = NonNullable<
  ErpAiKnowledgeDocumentQuery['aiKnowledgeDocument']
>;
export type AiKbStats = ErpAiKnowledgeStatsQuery['aiKnowledgeStats'];
export type AiKbHit = ErpAiKnowledgeSearchQuery['aiKnowledgeSearch'][number];

// ── Словари строк сервера ──────────────────────────────────────────────────

/**
 * Категории документа — порядок сервера (`ai_agents/kb/constants.py`
 * `CATEGORIES`, ТЗ 0118 B1): в нём блоки идут в промпт, в нём же стоят в
 * выпадашке. `style` — правила тона ответа, `company` — «как устроен
 * LearnIT», `marketing` в черновики клиентам не идёт никогда (решает
 * сервер).
 */
export const AI_KB_CATEGORIES = [
  'style',
  'company',
  'funding',
  'payments',
  'operations',
  'lms',
  'lessons',
  'sales',
  'marketing',
  'career',
  'other',
] as const;
export type AiKbCategory = typeof AI_KB_CATEGORIES[number];

export const isAiKbCategory = (value: unknown): value is AiKbCategory =>
  (AI_KB_CATEGORIES as readonly unknown[]).includes(value);

/** Категория нового документа, пока человек не выбрал свою. */
export const AI_KB_DEFAULT_CATEGORY: AiKbCategory = 'other';

/** Агенты видят только `published`; черновик и архив в промпт не идут. */
export const AI_KB_STATUSES = ['draft', 'published', 'archived'] as const;
export type AiKbStatus = typeof AI_KB_STATUSES[number];

export const isAiKbStatus = (value: unknown): value is AiKbStatus =>
  (AI_KB_STATUSES as readonly unknown[]).includes(value);

/**
 * Как база попадает к агенту (`aiKnowledgeStats.strategy`): `search` —
 * агент получает лучшие фрагменты гибридного поиска (FTS + векторы, ТЗ 0118
 * B6, по умолчанию), `prefix` — все опубликованные документы целиком в
 * начале промпта (откат: `AI_KB_RETRIEVAL_MODE=prefix`), `empty` —
 * опубликованного нет.
 */
export const AI_KB_STRATEGIES = ['prefix', 'search', 'empty'] as const;
export type AiKbStrategy = typeof AI_KB_STRATEGIES[number];

export const isAiKbStrategy = (value: unknown): value is AiKbStrategy =>
  (AI_KB_STRATEGIES as readonly unknown[]).includes(value);

/**
 * Куда можно перевести документ из его статуса — кнопки панели в этом
 * порядке: «Opublikuj» / «Przywróć do szkicu», затем «Archiwizuj».
 * Незнакомый статус — в черновик (вернуть под контроль человека).
 */
export const aiKbStatusTargets = (
  status: string | null | undefined,
): AiKbStatus[] => {
  if (status === 'draft') return ['published', 'archived'];
  if (status === 'published') return ['draft', 'archived'];
  if (status === 'archived') return ['draft', 'published'];
  return ['draft'];
};

/**
 * Коды отказов (`extensions.code`) — у каждого своя фраза
 * `aiKb.refusal.<код>`; незнакомый — общая (`generic`).
 */
export const AI_KB_REFUSALS = [
  'bad_input',
  'not_found',
  'forbidden',
  // ТЗ 0118 F3: документ сохранили в другой вкладке/другой человек, пока
  // шла правка (`expectedVersion` не совпал).
  'version_conflict',
] as const;
export type AiKbRefusal = typeof AI_KB_REFUSALS[number];

export const isAiKbRefusal = (value: unknown): value is AiKbRefusal =>
  (AI_KB_REFUSALS as readonly unknown[]).includes(value);

/**
 * Не отказ сервера, а «предыдущая отправка ещё едет» (вторая кнопка
 * за одну мутацию): его не показывают — ни фразой, ни сырым кодом.
 */
export const AI_KB_BUSY = 'busy';

export const aiKbIsBusy = (code: string | null | undefined): boolean =>
  code === AI_KB_BUSY;

/** Отказ «документ изменился, пока его правили» — с кнопкой обновления. */
export const AI_KB_VERSION_CONFLICT = 'version_conflict';

/** Фраза отказа: свой ключ у известного кода, общий — у остального. */
export const aiKbRefusalKey = (code: string): string =>
  isAiKbRefusal(code) ? `aiKb.refusal.${code}` : 'aiKb.refusal.generic';

// ── Адрес вкладки ──────────────────────────────────────────────────────────

/** Параметры адреса вкладки «Wiedza»: `?tab=knowledge&doc=5&category=lms`. */
export const AI_KB_PARAMS = {
  doc: 'doc',
  category: 'category',
  status: 'status',
  q: 'q',
} as const;

export interface IAiKbFilter {
  category: string | null;
  status: string | null;
  q: string | null;
}

// ── Загрузка файла ─────────────────────────────────────────────────────────

/** Расширения, из которых сервер извлекает текст (`UPLOAD_KINDS`). */
export const AI_KB_UPLOAD_EXTENSIONS = [
  'md',
  'markdown',
  'txt',
  'pdf',
  'docx',
] as const;

/** `accept` поля выбора файла. */
export const AI_KB_UPLOAD_ACCEPT = AI_KB_UPLOAD_EXTENSIONS.map(
  (ext) => `.${ext}`,
).join(',');

/** Предел сервера (`MAX_UPLOAD_BYTES`): текст извлекается прямо в запросе. */
export const AI_KB_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Что не так с файлом до отправки: тип, размер, пустой; `missing` — файл
 * не выбран. У каждого своя фраза `aiKb.upload.problem.<код>`.
 */
export const AI_KB_UPLOAD_PROBLEMS = [
  'type',
  'size',
  'empty',
  'missing',
] as const;
export type AiKbUploadProblem = typeof AI_KB_UPLOAD_PROBLEMS[number];

/** Проблема самого файла (`null` — годен). */
export type AiKbFileProblem = Exclude<AiKbUploadProblem, 'missing'>;

export const aiKbFileProblem = (
  file: Pick<File, 'name' | 'size'>,
): AiKbFileProblem | null => {
  const name = file.name ?? '';
  const ext = name.includes('.')
    ? name.slice(name.lastIndexOf('.') + 1).toLowerCase()
    : '';
  if (!(AI_KB_UPLOAD_EXTENSIONS as readonly string[]).includes(ext)) {
    return 'type';
  }
  if (file.size > AI_KB_UPLOAD_MAX_BYTES) return 'size';
  if (file.size === 0) return 'empty';
  return null;
};

/** Размер файла для подписи: «840 KB», «2,4 MB». */
export const formatAiKbFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

// ── Текст документа ────────────────────────────────────────────────────────

/**
 * Оценка токенов — та же, что у сервера (`estimate_tokens`:
 * `ceil(символы / 3.6)`): редактор показывает, сколько документ займёт в
 * промпте, ещё до сохранения.
 */
export const AI_KB_CHARS_PER_TOKEN = 3.6;

export const aiKbEstimateTokens = (text: string): number =>
  text ? Math.ceil(text.length / AI_KB_CHARS_PER_TOKEN) : 0;

/** Предел названия на сервере (`TITLE_MAX_LENGTH`). */
export const AI_KB_TITLE_MAX = 255;

export interface IAiKbDraft {
  title: string;
  category: string;
  body: string;
}

/** Чего не хватает форме до отправки: названия и/или текста. */
export const aiKbDraftProblems = (
  draft: IAiKbDraft,
): { title: boolean; body: boolean } => ({
  title: !draft.title.trim(),
  body: !draft.body.trim(),
});

/** Форма отличается от сохранённого — закрытие спрашивает подтверждения. */
export const aiKbDraftDirty = (
  draft: IAiKbDraft,
  saved: IAiKbDraft | null,
): boolean =>
  saved
    ? draft.title !== saved.title ||
      draft.category !== saved.category ||
      draft.body !== saved.body
    : Boolean(draft.title.trim() || draft.body.trim());

// ── Поиск ──────────────────────────────────────────────────────────────────

const RANK_FMT = new Intl.NumberFormat('pl-PL', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/** Ранг FTS — польской записью: «0,0759». */
export const formatAiKbRank = (rank: number | null | undefined): string =>
  typeof rank === 'number' && Number.isFinite(rank)
    ? RANK_FMT.format(rank)
    : '—';

// ── Ревизии документа (ТЗ 0118 B1) ─────────────────────────────────────────

export type AiKbRevision = ErpAiKnowledgeRevisionsQuery['aiKnowledgeRevisions'][number];

/**
 * Почему появилась версия (`reason`): `baseline` — снимок до истории,
 * `candidate` — одобренное предложение, `rollback` — возврат версии.
 * Незнакомая — как прислал сервер.
 */
export const AI_KB_REVISION_REASONS = [
  'baseline',
  'create',
  'edit',
  'upload',
  'import',
  'candidate',
  'rollback',
] as const;
export type AiKbRevisionReason = typeof AI_KB_REVISION_REASONS[number];

export const isAiKbRevisionReason = (
  value: unknown,
): value is AiKbRevisionReason =>
  (AI_KB_REVISION_REASONS as readonly unknown[]).includes(value);

/** Вернуть можно любую версию, кроме текущей (она и так в документе). */
export const aiKbCanRollback = (
  revision: Pick<AiKbRevision, 'version'>,
  currentVersion: number | null | undefined,
): boolean =>
  typeof currentVersion === 'number' && revision.version !== currentVersion;

// ── Предложения в базу знаний (ТЗ 0118 B2 / B4) ────────────────────────────

export type AiKbCandidatePage = ErpAiKbCandidatesQuery['aiKbCandidates'];
export type AiKbCandidate = AiKbCandidatePage['items'][number];
export type AiKbConflict = AiKbCandidate['conflicts'][number];
export type AiKbEvidence = AiKbCandidate['evidence'][number];
export type AiKbReviewerMe = ErpAiKbReviewerMeQuery['aiKbReviewerMe'];
export type AiKbReviewerCategory = ErpAiKbReviewersQuery['aiKbReviewers'][number];
export type AiKbReviewerUser = ErpAiKbReviewerCandidatesQuery['aiKbReviewerCandidates'][number];

/**
 * Вид предложения: `new_fact` — нового в базе нет, `contradiction` — ответ
 * сотрудника расходится с базой, `gap` — в базе нет ответа на вопрос
 * студента (одобряется только с ответом).
 */
export const AI_KB_CANDIDATE_KINDS = [
  'new_fact',
  'contradiction',
  'gap',
] as const;
export type AiKbCandidateKind = typeof AI_KB_CANDIDATE_KINDS[number];

export const isAiKbCandidateKind = (
  value: unknown,
): value is AiKbCandidateKind =>
  (AI_KB_CANDIDATE_KINDS as readonly unknown[]).includes(value);

/** Статус предложения; очередь по умолчанию — `pending`. */
export const AI_KB_CANDIDATE_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'duplicate',
] as const;
export type AiKbCandidateStatus = typeof AI_KB_CANDIDATE_STATUSES[number];

export const isAiKbCandidateStatus = (
  value: unknown,
): value is AiKbCandidateStatus =>
  (AI_KB_CANDIDATE_STATUSES as readonly unknown[]).includes(value);

/** Статус очереди без фильтра в адресе — ждут разбора. */
export const AI_KB_QUEUE_DEFAULT_STATUS: AiKbCandidateStatus = 'pending';

/**
 * «Любой статус» — в адресе словом (`?status=all`), серверу — пустой
 * строкой: отсутствие параметра значит «pending».
 */
export const AI_KB_QUEUE_ANY_STATUS = 'all';

/** Статус для запроса из значения адреса. */
export const aiKbQueueStatus = (raw: string | null): string => {
  if (raw === AI_KB_QUEUE_ANY_STATUS) return '';
  return raw || AI_KB_QUEUE_DEFAULT_STATUS;
};

/** Источник доказательства. */
export const AI_KB_EVIDENCE_SOURCES = [
  'gmail',
  'slack',
  'aplora_call',
  'reply_diff',
  'missing_info',
] as const;
export type AiKbEvidenceSource = typeof AI_KB_EVIDENCE_SOURCES[number];

export const isAiKbEvidenceSource = (
  value: unknown,
): value is AiKbEvidenceSource =>
  (AI_KB_EVIDENCE_SOURCES as readonly unknown[]).includes(value);

/** Строк на странице очереди (предел сервера по умолчанию). */
export const AI_KB_QUEUE_PAGE_SIZE = 50;

/**
 * Параметры адреса очереди — одни и те же на вкладке
 * (`/ai-agent?tab=proposals&…`) и на странице рецензента
 * (`/ai-agent/knowledge-review?…`): `candidate` — открытая карточка.
 */
export const AI_KB_QUEUE_PARAMS = {
  candidate: 'candidate',
  category: 'category',
  kind: 'kind',
  status: 'status',
  offset: 'offset',
} as const;

/** Смещение из адреса: не число или меньше нуля — первая страница. */
export const parseAiKbQueueOffset = (raw: string | null): number => {
  const num = Number(raw);
  return Number.isInteger(num) && num > 0 ? num : 0;
};

/**
 * Страница рецензента (не Admin) — с параметрами очереди. Путь — из
 * `PATHS`, не шаблонной строкой.
 */
export const aiKbReviewHref = (
  params: Partial<Record<keyof typeof AI_KB_QUEUE_PARAMS, string>> = {},
): string => {
  const out = new URLSearchParams();
  Object.entries(params).forEach(([name, value]) => {
    if (value) out.set(name, value);
  });
  const qs = out.toString();
  return qs ? `${PATHS.aiKbReview}?${qs}` : PATHS.aiKbReview;
};

/**
 * Пункт меню «Propozycje do bazy wiedzy» и команда палитры — рецензенту
 * без Admin с назначенными категориями. Admin разбирает на вкладке
 * «Propozycje» страницы «ИИ-агент». Авторитет — у сервера.
 */
export const aiKbShowsReviewEntry = (
  me: Pick<AiKbReviewerMe, 'isAdmin' | 'categories'> | null | undefined,
): boolean => Boolean(me && !me.isAdmin && me.categories.length > 0);

/** Уверенность модели — целыми процентами; нет — `null`. */
export const aiKbConfidencePercent = (
  value: number | null | undefined,
): number | null =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.round(Math.min(1, Math.max(0, value)) * 100)
    : null;

/**
 * Строки противоречащего фрагмента, которые можно заменить (`mode=replace`):
 * непустые строки текста, кроме заголовков. Сервер ищет строку в теле
 * документа без учёта пробелов по краям.
 */
export const aiKbConflictLines = (
  conflict: Pick<AiKbConflict, 'text'>,
): string[] =>
  (conflict.text ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'));

export type AiKbApproveMode = 'append' | 'replace';

/** Что заменить при одобрении противоречия: строка и её документ. */
export interface IAiKbReplaceTarget {
  documentId: string | null;
  line: string;
}

export interface IAiKbApproveDraft {
  /** Правило из формы (может быть исправлено). */
  claim: string;
  /** Ответ на пробел (`gap`). */
  answer: string;
  mode: AiKbApproveMode;
  replace: IAiKbReplaceTarget | null;
  /** «Edytuj i zatwierdź»: правило отправляется исправленным. */
  edited: boolean;
  /** Исправленный заголовок секции; `null` — как у предложения. */
  heading?: string | null;
}

/** Чего не хватает для одобрения; `null` — можно отправлять. */
export type AiKbApproveProblem = 'answer' | 'claim' | 'replaceLine';

export const aiKbApproveProblem = (
  candidate: Pick<AiKbCandidate, 'kind' | 'claim'>,
  draft: IAiKbApproveDraft,
): AiKbApproveProblem | null => {
  if (candidate.kind === 'gap') {
    return draft.answer.trim() ? null : 'answer';
  }
  const claim = draft.edited ? draft.claim : candidate.claim;
  if (!claim.trim()) return 'claim';
  if (draft.mode === 'replace' && !draft.replace?.line.trim()) {
    return 'replaceLine';
  }
  return null;
};

/** Переменные `approveAiKbCandidate` из формы карточки. */
export const aiKbApproveVariables = (
  candidate: Pick<AiKbCandidate, 'id' | 'kind'>,
  draft: IAiKbApproveDraft,
) => {
  const gap = candidate.kind === 'gap';
  const replace =
    !gap && draft.mode === 'replace' && draft.replace ? draft.replace : null;
  return {
    id: candidate.id,
    claim: !gap && draft.edited ? draft.claim.trim() : null,
    answer: gap ? draft.answer.trim() : null,
    mode: replace ? 'replace' : 'append',
    replaceLine: replace ? replace.line : null,
    targetDocumentId: replace?.documentId ?? null,
    targetHeading: draft.heading?.trim() || null,
  };
};

/**
 * Ссылка на диалог коммуникатора, из которого взято доказательство.
 * Коммуникатор открывает диалог только внутри группы (`communicatorLink`),
 * поэтому без группы ссылки нет — подпись остаётся текстом.
 */
export const aiKbEvidenceHref = (
  evidence: Pick<AiKbEvidence, 'conversationId'> & {
    groupId?: string | null;
    studentId?: string | null;
  },
): string | null => {
  const conv = evidence.conversationId ? String(evidence.conversationId) : '';
  const group = evidence.groupId ? String(evidence.groupId) : '';
  if (!conv || !group) return null;
  return communicatorLink({
    group,
    student: evidence.studentId ? String(evidence.studentId) : '',
    ctx: evidence.studentId ? '' : 'channels',
    conv,
  });
};

/**
 * Коды отказов разбора — у каждого своя фраза `aiKb.review.refusal.<код>`;
 * незнакомый — общая.
 */
export const AI_KB_REVIEW_REFUSALS = [
  'bad_input',
  'not_found',
  'forbidden',
] as const;

export const aiKbReviewRefusalKey = (code: string | null | undefined): string =>
  (AI_KB_REVIEW_REFUSALS as readonly unknown[]).includes(code)
    ? `aiKb.review.refusal.${code}`
    : 'aiKb.review.refusal.generic';

// ── Обслуживание базы (ТЗ 0118, шаг 2) ─────────────────────────────────────

/** Статус последнего ночного прохода векторов (`embed_sweep`). */
export const AI_KB_SWEEP_STATUSES = [
  'running',
  'success',
  'skipped',
  'failed',
] as const;
export type AiKbSweepStatus = typeof AI_KB_SWEEP_STATUSES[number];

export const isAiKbSweepStatus = (value: unknown): value is AiKbSweepStatus =>
  (AI_KB_SWEEP_STATUSES as readonly unknown[]).includes(value);

/**
 * Предупредить о семантическом поиске: не у всех кусков опубликованного
 * есть вектор или последний ночной проход упал.
 */
/**
 * Почему векторный поиск сейчас не работает (`aiKnowledgeStats.
 * vectorsOffReason`, ТЗ 0118 F7): у каждой причины своя фраза
 * `aiKb.vectors.off.<код>`, незнакомая — общая с кодом.
 */
export const AI_KB_VECTORS_OFF_REASONS = [
  'disabled',
  'no_api_key',
  'stand',
  'no_vector_table',
] as const;
export type AiKbVectorsOffReason = typeof AI_KB_VECTORS_OFF_REASONS[number];

export const isAiKbVectorsOffReason = (
  value: string | null | undefined,
): value is AiKbVectorsOffReason =>
  (AI_KB_VECTORS_OFF_REASONS as ReadonlyArray<string>).includes(value ?? '');

export const aiKbVectorsNeedAttention = (
  stats: Pick<AiKbStats, 'vectorCoverage' | 'lastSweepStatus'> | null,
): boolean => {
  if (!stats) return false;
  const { withVector, total } = stats.vectorCoverage;
  return withVector < total || stats.lastSweepStatus === 'failed';
};
